import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  LinearProgress,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import apiClient from '../api/client';

function BacktestResult() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('queued');
  const [result, setResult] = useState(null);

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 2000); // Poll every 2 seconds
    return () => clearInterval(interval);
  }, [taskId]);

  const checkStatus = async () => {
    try {
      const statusResponse = await apiClient.get(`/backtest/${taskId}/status`);
      setStatus(statusResponse.data.status);

      if (statusResponse.data.status === 'finished') {
        loadResult();
        setLoading(false);
      } else if (statusResponse.data.status === 'error') {
        setError(statusResponse.data.error || 'Backtest failed');
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to check status: ' + err.message);
      setLoading(false);
    }
  };

  const loadResult = async () => {
    try {
      const response = await apiClient.get(`/backtest/${taskId}/result`);
      setResult(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load results: ' + err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Button onClick={() => navigate('/')} sx={{ mb: 2 }}>
          ← Back to Dashboard
        </Button>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Running Backtest...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Status: {status}
          </Typography>
          <CircularProgress />
          {status === 'running' && <LinearProgress sx={{ mt: 2 }} />}
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button onClick={() => navigate('/')} sx={{ mb: 2 }}>
          ← Back to Dashboard
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!result) {
    return null;
  }

  const metrics = result.metrics || {};
  const equityData = result.equity_curve?.map((point) => ({
    date: new Date(point.timestamp).toLocaleDateString(),
    equity: point.equity,
    exposure: point.exposure_pct * 100,
  })) || [];

  return (
    <Box>
      <Button onClick={() => navigate('/')} sx={{ mb: 2 }}>
        ← Back to Dashboard
      </Button>

      <Typography variant="h4" gutterBottom>
        Backtest Results: {result.symbol}
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Metrics Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total PnL
              </Typography>
              <Typography variant="h5">
                {metrics.total_pnl_pct?.toFixed(2)}%
              </Typography>
              <Typography variant="body2" color={metrics.total_pnl_pct >= 0 ? 'success.main' : 'error.main'}>
                ${metrics.total_pnl?.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Sharpe Ratio
              </Typography>
              <Typography variant="h5">
                {metrics.sharpe_ratio?.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Max Drawdown
              </Typography>
              <Typography variant="h5" color="error.main">
                {metrics.max_drawdown_pct?.toFixed(2)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Win Rate
              </Typography>
              <Typography variant="h5">
                {metrics.win_rate?.toFixed(2)}%
              </Typography>
              <Typography variant="body2">
                {metrics.winning_trades}/{metrics.total_trades} trades
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Equity Curve Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Equity Curve
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={equityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="equity" stroke="#8884d8" name="Equity" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Additional Metrics */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Performance Metrics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Sortino Ratio
                </Typography>
                <Typography variant="h6">{metrics.sortino_ratio?.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Profit Factor
                </Typography>
                <Typography variant="h6">{metrics.profit_factor?.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Avg Profit
                </Typography>
                <Typography variant="h6">${metrics.avg_profit?.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Avg Loss
                </Typography>
                <Typography variant="h6">${metrics.avg_loss?.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  R/R Ratio
                </Typography>
                <Typography variant="h6">{metrics.reward_risk_ratio?.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Total Trades
                </Typography>
                <Typography variant="h6">{metrics.total_trades}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Trade List */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Trades
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Entry</TableCell>
                    <TableCell>Exit</TableCell>
                    <TableCell>PnL</TableCell>
                    <TableCell>PnL %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.trades?.slice(0, 10).map((trade) => (
                    <TableRow key={trade.id}>
                      <TableCell>${trade.entry_price?.toFixed(2)}</TableCell>
                      <TableCell>${trade.exit_price?.toFixed(2)}</TableCell>
                      <TableCell style={{ color: trade.pnl >= 0 ? 'green' : 'red' }}>
                        ${trade.pnl?.toFixed(2)}
                      </TableCell>
                      <TableCell style={{ color: trade.pnl_pct >= 0 ? 'green' : 'red' }}>
                        {trade.pnl_pct?.toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default BacktestResult;

